import { create } from 'zustand';
import { setAccessToken } from '../services/http.js';

/**
 * Session state. Zustand holds it because it is genuinely client state: which
 * account this tab is acting as, and the in-memory access token.
 *
 * Nothing here is persisted. On a reload the app calls POST /auth/refresh once
 * (the refresh token is an httpOnly cookie the JS cannot read), and either gets a
 * new session or settles on `anonymous`. Persisting the user object instead would
 * mean the UI can claim you are signed in — or claim you are an admin — after the
 * server has already revoked the session.
 *
 * `role` here only ever decides what is *shown*. Every admin capability is
 * enforced again server-side on the route that performs it; see the backend's
 * requireRole middleware. A tampered store gets a 403, not a privilege.
 */
export const useAuthStore = create((set) => ({
  user: null,

  /** 'unknown' until the first refresh attempt resolves. */
  status: 'unknown', // 'unknown' | 'authenticated' | 'anonymous'

  /** Called by the auth service after login, signup or a successful refresh. */
  setSession({ user, accessToken }) {
    setAccessToken(accessToken);
    set({ user, status: 'authenticated' });
  },

  /** Fresh access token from a silent refresh; the user object is unchanged. */
  setAccessToken(accessToken) {
    setAccessToken(accessToken);
  },

  /** A profile edit: same session, new user object. */
  setUser(user) {
    set({ user });
  },

  /** Logout, a revoked session, or a failed initial refresh all land here. */
  clearSession() {
    setAccessToken(null);
    set({ user: null, status: 'anonymous' });
  },
}));

/* Selectors — components subscribe to these rather than to the whole store, so a
   token refresh does not re-render the header. */
export const selectUser = (s) => s.user;
export const selectIsAuthenticated = (s) => s.status === 'authenticated';
export const selectIsAdmin = (s) => s.user?.role === 'admin';
export const selectAuthResolved = (s) => s.status !== 'unknown';

export default useAuthStore;
